import moment from "moment";
import { PtApiError, ptRequest } from "pages/PayRoll/Configuration/StatutoryConfiguration/PT/ptConfigApi";

// PT Employees (screens PT2–PT2b of the PT Frontend API Guide), base
// /api/payroll/statutory/pt/. The list and the details drawer are worked
// out live by the server for the first open payroll month; marking and
// ending an exemption are the only saves. Uses ptRequest from the PT
// Configuration API (token, 401 sign-out, field errors). The optional
// Idempotency-Key is not sent (CORS, same as ESI and LWF); Save shows a
// loading state instead.

const text = (value) => (value == null ? "" : String(value));
const upper = (value) => String(value || "").toUpperCase();
const list = (value) => (Array.isArray(value) ? value.filter((item) => item && typeof item === "object") : []);

export const STATUS_TONES = { PAYS_PT: "green", EXEMPT: "blue", SITE_NOT_SET_UP: "red", HELD: "amber" };
export const monthLabel = (monthKey) => (monthKey ? moment(`${monthKey}-01`).format("MMM YYYY") : "");

// One row of GET employees/ (also the drawer's employee block).
const fromApiEmployee = (row) => {
  const status = upper(row.status);
  const exemption = row.exemption && typeof row.exemption === "object" ? row.exemption : null;
  return {
    id: row.employee_id,
    emp_id: text(row.employee_code),
    name: text(row.name),
    department: text(row.department),
    site_id: row.site?.id ?? null,
    site_name: text(row.site?.name),
    frequency_label: text(row.deducted_label),
    dob: text(row.dob),
    age: row.age ?? null,
    dob_label: row.dob ? `${moment(row.dob).format("DD MMM YYYY")}${row.age != null ? ` (${row.age})` : ""}` : "",
    pt_wage: row.pt_wage == null || row.pt_wage === "" ? null : Number(row.pt_wage),
    status,
    pt_status: text(row.status_label) || status,
    pt_status_tone: STATUS_TONES[status] || "slate",
    exemption,
    exemption_label: text(exemption?.label),
  };
};

// GET employees/ — cards, one page of rows and the month it is worked out for.
export const fetchPtEmployees = async ({ domain, companyId, siteId, status, search, page, pageSize, signal }) => {
  const data = await ptRequest({
    domain,
    path: "employees/",
    query: { company_id: companyId, site_id: siteId, status, search, page, page_size: pageSize },
    signal,
  });
  if (!data || typeof data !== "object") throw new PtApiError("The server returned an invalid PT employees list.");
  const results = Array.isArray(data) ? data : data.results;
  const rows = list(results).map(fromApiEmployee);
  const cards = data.cards || {};
  return {
    month: text(data.month),
    month_label: text(data.month_label) || monthLabel(data.month),
    cards: {
      all: cards.all ?? 0,
      pays_pt: cards.pays_pt ?? 0,
      exempt: cards.exempt ?? 0,
      site_not_set_up: cards.site_not_set_up ?? 0,
      held: cards.held ?? 0,
    },
    count: data.count ?? rows.length,
    rows,
  };
};

// Site filter options: GET config/sites/ (site id + name).
export const fetchPtSiteOptions = async ({ domain, companyId, signal }) => {
  const data = await ptRequest({ domain, path: "config/sites/", query: { company_id: companyId }, signal });
  return list(data?.sites)
    .map((row) => ({ value: String(row.site?.id ?? ""), label: text(row.site?.name) }))
    .filter((option) => option.value && option.label);
};

// GET employees/<id>/ — the Employee PT Details drawer.
export const fetchPtEmployee = async ({ domain, companyId, employeeId, signal }) => {
  const data = await ptRequest({ domain, path: `employees/${employeeId}/`, query: { company_id: companyId }, signal });
  if (!data?.employee) throw new PtApiError("The server returned invalid PT details.");
  const period = data.this_period && typeof data.this_period === "object" ? data.this_period : null;
  const exemption = data.exemption && typeof data.exemption === "object" ? data.exemption : { label: "None" };
  return {
    month: text(data.month),
    month_label: text(data.month_label) || monthLabel(data.month),
    row: fromApiEmployee(data.employee),
    pt_site: { site: text(data.pt_site?.site), note: text(data.pt_site?.note) },
    how_collected: text(data.how_collected),
    exemption: {
      id: exemption.id ?? null,
      label: text(exemption.label) || "None",
      auto: Boolean(exemption.auto),
      period: text(exemption.period),
      from_month: text(exemption.from_month || exemption.from),
      remarks: text(exemption.remarks),
      ended: text(exemption.ended),
    },
    this_period: period
      ? {
          label: text(period.label),
          pt_wage_so_far: period.pt_wage_so_far,
          so_far_note: text(period.so_far_note),
          expected: text(period.expected),
          collected: period.collected,
          collected_note: text(period.collected_note),
        }
      : null,
    history: list(data.history).map((step) => ({ label: text(step.label), date: text(step.text), done: upper(step.state) === "DONE" })),
    actions: { mark_exempt: Boolean(data.actions?.mark_exempt), end_exemption: Boolean(data.actions?.end_exemption) },
  };
};

// GET employees/<id>/exemption-reasons/ — the HR-marked reasons of the
// employee's site (age reasons are automatic and never listed).
export const fetchPtExemptionReasons = async ({ domain, companyId, employeeId, signal }) => {
  const data = await ptRequest({ domain, path: `employees/${employeeId}/exemption-reasons/`, query: { company_id: companyId }, signal });
  return {
    site_name: text(data?.site?.name),
    month: text(data?.month),
    month_label: text(data?.month_label) || monthLabel(data?.month),
    reasons: list(data?.reasons).map((reason) => ({
      id: reason.id,
      reason: text(reason.reason),
      rule_text: text(reason.rule_text),
      proof_required: Boolean(reason.proof_required),
    })),
    choices_text: text(data?.choices_text),
    attachments_enabled: Boolean(data?.attachments_enabled),
  };
};

// Server field -> drawer field.
const FIELD_KEYS = { reason_id: "reason", from_month: "from", to_month: "to", proof_file: "proof" };
const withFields = (error) => {
  if (error instanceof PtApiError && error.fieldErrors) {
    error.fields = Object.fromEntries(Object.entries(error.fieldErrors).map(([key, message]) => [FIELD_KEYS[key] || key, message]));
  }
  return error;
};

// POST employees/<id>/exemptions/ -> { message, data }.
export const markPtExempt = async ({ domain, companyId, employeeId, reasonId, fromMonth, remarks }) => {
  try {
    return await ptRequest({
      domain,
      path: `employees/${employeeId}/exemptions/`,
      method: "POST",
      body: { company_id: companyId, reason_id: reasonId, from_month: fromMonth, ...(remarks ? { remarks } : {}) },
    });
  } catch (error) {
    throw withFields(error);
  }
};

// POST exemptions/<id>/end/ with the last exempt month -> { message, data }.
export const endPtExemption = async ({ domain, companyId, exemptionId, toMonth }) => {
  try {
    return await ptRequest({ domain, path: `exemptions/${exemptionId}/end/`, method: "POST", body: { company_id: companyId, to_month: toMonth } });
  } catch (error) {
    throw withFields(error);
  }
};

// Months from `first` on, as select options.
export const monthOptions = (first, count = 12) =>
  first
    ? Array.from({ length: count }, (_, index) => {
        const month = moment(`${first}-01`).add(index, "months");
        return { value: month.format("YYYY-MM"), label: month.format("MMM YYYY") };
      })
    : [];
